import { AppreciationPrice, ProductAppreciationNote } from "./ReaderAppreciation";
import { displayLabel } from "../../utils/displayLabel";
import TbrButton from "../ui/TbrButton";
import BookRating from "./BookRating";
import SEOHead from "../ui/SEOHead";
// components/public/ProductPage.js
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import CustomPageRenderer from "../authorportal/CustomPageRenderer";
import { fetchAuthorFolderForHost, fetchCustomPageBySlug } from "./CustomPagesApi";
import CheckoutModal from "./CheckoutModal";
import "./ProductPage.css";
import { addToAuthorCart } from "./AuthorCart";

const GENRE_ALIASES = {
  romantasy: "Romantasy",
  "romantic fantasy": "Romantasy",
  "fantasy romance": "Romantasy",
};

const formatGenreDisplay = (genre) => {
  const clean = String(genre || "").trim();
  if (!clean) return "";
  return GENRE_ALIASES[clean.toLowerCase()] || clean;
};

const BUCKET_BASE_URL =
  "https://bookish-master-data.s3.us-west-2.amazonaws.com/AuthorShops";
const PLACEHOLDER = "https://placehold.co/320x480?text=No+Image";

const getNumericPrice = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY;
};

const sortFormatsByPrice = (formats = []) =>
  [...formats].sort((a, b) => {
    const priceDiff = getNumericPrice(a?.price) - getNumericPrice(b?.price);
    if (priceDiff !== 0) return priceDiff;
    return String(a?.type || "").localeCompare(String(b?.type || ""));
  });

const getFormatImages = (format = {}) => {
  if (Array.isArray(format?.coverImages) && format.coverImages.length) {
    return format.coverImages.map((image) => image?.url).filter(Boolean);
  }
  return format?.coverImageUrl ? [format.coverImageUrl] : [];
};

const getVariationImages = (variation = {}) =>
  (Array.isArray(variation?.images) ? variation.images : [])
    .map((image) => image?.url)
    .filter(Boolean)
    .slice(0, 3);

export default function ProductPage() {
  const { bookslug } = useParams();
  const [book, setBook] = useState(null);
  const [customPage, setCustomPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedFormatIndex, setSelectedFormatIndex] = useState(0);
  const [selectedVariationIndex, setSelectedVariationIndex] = useState(-1);
  const [formatChosen, setFormatChosen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);

  useEffect(() => {
    const fetchBook = async () => {
      try {
        const subdomain = window.location.hostname;
        const res = await fetch(`${BUCKET_BASE_URL}/${subdomain}.json`);

        const data = await res.json();

        const slugify = (t) =>
          String(t || "")
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "");

        const found = (data.books || []).find((b) => {
          return (
            String(b.slug || "").toLowerCase() === bookslug ||
            slugify(b.title) === bookslug
          );
        });

        if (found) {
          setBook(found);
          setCustomPage(null);
          return;
        }

        const authorFolder =
          await fetchAuthorFolderForHost(subdomain);

        if (authorFolder) {
          const pageData = await fetchCustomPageBySlug({
            authorFolder,
            slug: bookslug,
          });
          if (pageData) {
            setCustomPage(pageData);
            setBook(null);
            return;
          }
        }

        setBook(null);
        setCustomPage(null);
      } catch (err) {
        console.error("❌ Failed loading book/page:", err);
        setBook(null);
        setCustomPage(null);
      } finally {
        setLoading(false);
      }
    };

    fetchBook();
  }, [bookslug]);

  const sortedFormats = useMemo(
    () => sortFormatsByPrice(book?.formats || []),
    [book]
  );

  const selectedFormat = sortedFormats[selectedFormatIndex] || null;

  useEffect(() => {
    if (!sortedFormats.length) {
      setSelectedFormatIndex(0);
      return;
    }

    setSelectedFormatIndex((currentIndex) =>
      currentIndex >= 0 && currentIndex < sortedFormats.length ? currentIndex : 0
    );
  }, [sortedFormats]);

  useEffect(() => {
    setSelectedVariationIndex(-1);
    setSelectedImageIndex(0);
    setAddedToCart(false);
  }, [selectedFormatIndex]);

  const handleFormatSelect = (formatIndex) => {
    setFormatChosen(true);
    setSelectedFormatIndex(formatIndex);
    setSelectedVariationIndex(-1);
    setSelectedImageIndex(0);
    setAddedToCart(false);
  };

  const isEbookFormat = (format = {}) =>
    String(format?.type || "")
      .toLowerCase()
      .replace(/\s+/g, "") === "ebook";

  const getFulfillmentType = (format = {}) =>
    String(format?.fulfillmentType || "").toLowerCase();

  const canBuyDirect = (format = {}) => {
    if (!format) return false;

    if (isEbookFormat(format)) {
      return getFulfillmentType(format) !== "external";
    }

    if (getFulfillmentType(format) === "external") return false;
    return Boolean(format?.isDirect);
  };

  const handleBuyNow = (format) => {
    if (!canBuyDirect(format)) return;
    setIsCheckoutOpen(true);
  };

  const handleAddToCart = (format) => {
    if (!canBuyDirect(format) || !book) return;

    const variationName = format?.variationName || "";
    const formatType = format?.type || "";

    const cartItemId = [
      bookslug,
      formatType,
      variationName || "standard",
    ]
      .map((part) => String(part).toLowerCase().trim())
      .join("::");

    const cartItem = {
      cartItemId,

      bookslug,
      bookId: book?.bookId || book?.id || "",
      title: book?.title || "",
      authorName: book?.authorName || "",

      format: formatType,
      variationName,
      selectedVariation: format?.selectedVariation || null,

      price: Number(format?.price || 0),
      quantity: 1,
      stockQuantity: Number(format?.quantity ?? 0),

      coverImageUrl:
        format?.coverImageUrl ||
        book?.coverImageUrl ||
        "",

      fulfillmentType: format?.fulfillmentType || "",
      isDirect: Boolean(format?.isDirect),

      weight: format?.weight ?? null,
      length: format?.length ?? null,
      width: format?.width ?? null,
      height: format?.height ?? null,

      mediaMailEligible:
        format?.mediaMailEligible ?? false,

      shipping: format?.shipping || {},
    };

    addToAuthorCart(cartItem);

    setAddedToCart(true);

    window.setTimeout(() => {
      setAddedToCart(false);
    }, 1500);
  };

  const selectedVariation =
    selectedFormat && selectedVariationIndex > -1
      ? (selectedFormat.variations || [])[selectedVariationIndex] || null
      : null;

  const selectedImages =
    getVariationImages(selectedVariation).length > 0
      ? getVariationImages(selectedVariation)
      : getFormatImages(selectedFormat);

  const resolvedImage = selectedImages[selectedImageIndex] || selectedImages[0] || selectedFormat?.coverImageUrl || PLACEHOLDER;
  const resolvedPrice =
    selectedVariation?.price !== undefined && selectedVariation?.price !== null && selectedVariation?.price !== ""
      ? selectedVariation.price
      : selectedFormat?.price;
  const resolvedQuantity = selectedVariation
    ? selectedVariation.useBaseQuantity === false
      ? selectedVariation.quantity
      : selectedFormat?.quantity
    : selectedFormat?.quantity;
  const checkoutSelection = selectedFormat
    ? {
        ...selectedFormat,
        price: resolvedPrice,
        quantity: resolvedQuantity,
        coverImageUrl: resolvedImage,
        selectedVariation: selectedVariation || null,
        variationName: selectedVariation?.name || "",
      }
    : null;



  if (loading)
    return <div className="p-10 text-bookish-purple">Loading details...</div>;

  if (customPage) return <CustomPageRenderer page={customPage} />;

  if (!book)
    return <div className="p-10 text-bookish-purple">Page not found.</div>;

  return (
    <div className="product-page min-h-screen px-4 py-6 md:px-8">
      <SEOHead title={displayLabel(book.title) + " | " + displayLabel(book.authorName || "The Bookish Circle")} description={book.tagline || "Discover this independent book on The Bookish Circle."} image={resolvedImage} url={window.location.href} />
      <div className="max-w-6xl mx-auto">
        <div className="product-grid">
          <div className="product-intro">
            <h1 className="mb-4 text-3xl md:text-4xl font-serif font-bold text-bookish-purple">
              {displayLabel(book.title)}
            </h1>
            <p className="mb-2 text-xl font-serif font-semibold text-bookish-purpleAccent">
              by <a href="/" className="underline underline-offset-4">{displayLabel(book.authorName)}</a>
            </p>

            {book.genres && (
              <div className="mb-6 flex flex-wrap gap-2">
                {(Array.isArray(book.genres)
                  ? book.genres
                  : String(book.genres).split(",")
                )
                  .map(formatGenreDisplay)
                  .filter(Boolean)
                  .map((g, i) => (
                    <span
                      key={i}
                      className="rounded-full bg-bookish-purple/10 px-3 py-1 text-xs text-bookish-purple"
                    >
                      {displayLabel(g)}
                    </span>
                  ))}
              </div>
            )}

          </div>
          <div className="product-gallery">
                    <div className="relative">
                      <img
                        src={resolvedImage}
                        alt={`${displayLabel(selectedFormat?.type || book.title)} cover`}
                        className="mx-auto mb-4 h-[280px] md:h-[360px] w-full object-contain"
                        onError={(e) => {
                          e.currentTarget.src = PLACEHOLDER;
                        }}
                      />
                      {selectedImages.length > 1 && (
                        <>
                          <button
                            type="button"
                            onClick={() => setSelectedImageIndex((prev) => (prev - 1 + selectedImages.length) % selectedImages.length)}
                            className="absolute left-1 top-1/2 -translate-y-1/2 rounded-full border border-bookish-purple/20 bg-white px-2 py-1 text-bookish-purple"
                            aria-label="Previous image"
                          >
                            ‹
                          </button>
                          <button
                            type="button"
                            onClick={() => setSelectedImageIndex((prev) => (prev + 1) % selectedImages.length)}
                            className="absolute right-1 top-1/2 -translate-y-1/2 rounded-full border border-bookish-purple/20 bg-white px-2 py-1 text-bookish-purple"
                            aria-label="Next image"
                          >
                            ›
                          </button>
                        </>
                      )}
                    </div>


            <BookRating productUrl={`https://${window.location.hostname}/shop/${bookslug}`} />
            {book.tagline && <p className="product-cover-tagline">{book.tagline}</p>}
            <div id="product-purchase" className="product-purchase" tabIndex={-1}>
              {sortedFormats.length > 0 && (
                <>
                  <div className="product-format-options" role="group" aria-label="Available formats">
                    {sortedFormats.map((format, idx) => {
                      const isSelected = idx === selectedFormatIndex;

                      return (
                        <button
                          key={`${format?.type || "format"}-${idx}`}
                          type="button"
                          onClick={() => handleFormatSelect(idx)}
                          aria-pressed={isSelected}
                          aria-label={displayLabel(format?.type || `Format ${idx + 1}`)}
                          aria-describedby={`format-price-${idx}`}
                          className="product-format-row"
                        >
                          <span>{displayLabel(format?.type || `Format ${idx + 1}`)}</span>
                          <span id={`format-price-${idx}`} className="product-format-price" aria-label={isSelected ? "Selected price" : undefined}><AppreciationPrice price={isSelected ? resolvedPrice : format.price} format={format.type} compact /></span>
                        </button>
                      );
                    })}
                  </div>

                  {selectedFormat && (
                    <div className="product-purchase-details">
                      {selectedFormat?.shipping?.freeShipping && (
                        <p className="mt-2 text-sm font-semibold text-bookish-purpleAccent">
                          Free Shipping
                        </p>
                      )}
                      <ProductAppreciationNote price={resolvedPrice} format={selectedFormat.type} />

                      {formatChosen && Array.isArray(selectedFormat.variations) &&
                        selectedFormat.variations.length > 0 && (
                          <div className="product-edition-options mt-2 flex flex-wrap gap-3 text-sm" role="group" aria-label="Available variations">
                            <button
                              type="button"
                              aria-pressed={selectedVariationIndex === -1}
                              className={`underline ${selectedVariationIndex === -1 ? "text-bookish-purple font-semibold" : "text-bookish-purple/80"}`}
                              onClick={() => {
                                setSelectedVariationIndex(-1);
                                setSelectedImageIndex(0);
                                setAddedToCart(false);
                              }}
                            >
                              Standard
                            </button>
                            {selectedFormat.variations.map((variation, variationIdx) => (
                              <button
                                key={`${variation?.name || "variation"}-${variationIdx}`}
                                type="button"
                                aria-pressed={selectedVariationIndex === variationIdx}
                                className={`underline ${
                                  selectedVariationIndex === variationIdx
                                    ? "text-bookish-purple font-semibold"
                                    : "text-bookish-purple/80"
                                }`}
                                onClick={() => {
                                  setSelectedVariationIndex(variationIdx);
                                  setSelectedImageIndex(0);
                                  setAddedToCart(false);
                                }}
                              >
                                {displayLabel(variation?.name || `Variation ${variationIdx + 1}`)}
                              </button>
                            ))}
                          </div>
                        )}

                      {selectedVariation?.extrainfo && (
                        <div className="mt-3 rounded-lg border border-bookish-purple/10 bg-bookish-cream/40 px-4 py-3 text-sm leading-6 text-bookish-purple/80 whitespace-pre-line">
                          {selectedVariation.extrainfo}
                        </div>
                      )}
                      {resolvedQuantity !== undefined && resolvedQuantity !== "" && (
                        <p className="mt-2 text-sm text-bookish-purple/70">In stock: {resolvedQuantity}</p>
                      )}

                      <div className="product-purchase-actions mt-4 flex flex-col gap-3">

                        {canBuyDirect(checkoutSelection) && (
                        <>
                          <button
                            type="button"
                            onClick={() => handleAddToCart(checkoutSelection)}
                            disabled={addedToCart}
                            className="rounded-md bg-bookish-royal px-4 py-2 text-white text-large transition hover:bg-bookish-purple disabled:opacity-70"
                          >
                            {addedToCart ? "Added to Cart ✓" : "Add to Cart"}
                          </button>

                          <button
                            type="button"
                            onClick={() => handleBuyNow(checkoutSelection)}
                            className="rounded-md border border-bookish-purple px-4 py-2 text-bookish-purple text-large transition hover:bg-bookish-purple/5"
                          >
                            Buy Now
                          </button>
                        </>
                      )}


                        {Array.isArray(selectedFormat.externalLinks) &&
                          selectedFormat.externalLinks.length > 0 &&
                          selectedFormat.externalLinks.map((link, i) => (
                            <a
                              key={i}
                              href={
                                link.url?.startsWith("http")
                                  ? link.url
                                  : `https://${link.url}`
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-center gap-1 rounded-md px-3 py-2 text-bookish-purple transition hover:bg-bookish-purple/5"
                            >
                              {link.label || "Buy Here"}
                              <ExternalLink size={14} />
                            </a>
                          ))}
                      </div>
                      <div className="product-reading-list"><TbrButton book={book} productUrl={book.productUrl || window.location.href} coverImageUrl={resolvedImage} /></div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          <section className="product-description" aria-labelledby="product-description-heading"><h2 id="product-description-heading">About the book</h2>
            <div className="whitespace-pre-line text-[1.05rem] leading-8 text-bookish-purple/80">
              {book.blurb || "No description available yet."}
            </div>
          </section>



        </div>
      </div>
      <CheckoutModal
        isOpen={isCheckoutOpen}
        onClose={() => setIsCheckoutOpen(false)}
        book={book}
        format={checkoutSelection || selectedFormat}
        bookslug={bookslug}
      />
    </div>
  );
}
